import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUp, BarChart3, Check, Clock, Filter, Lock, Mail, MessageSquare, Pause, Play, Plus, Repeat, Search, Siren, Sparkles, UserPlus, Users, Workflow } from 'lucide-react';
import { AppChrome, BrandLogo } from './AppChrome';
import { SectionIntro } from './SectionIntro';
import { ProductVignette } from './ProductVignette';
import { useStill } from './stillContext';
import { HUES, avatarHue } from './hues';
type PrimitiveId = 'chats' | 'shared' | 'workflows' | 'pulses' | 'skills';
const PRIMITIVES: Array<{
  id: PrimitiveId;
  label: string;
  icon: React.ElementType;
  copy: string;
}> = [{
  id: 'skills',
  label: 'Skills',
  icon: Sparkles,
  copy: 'Your best person writes down how they do something once, then shares it to a group — and everyone in it can run their method.'
}, {
  id: 'workflows',
  label: 'Workflows',
  icon: Workflow,
  copy: 'Turn a process you repeat into defined steps with declared inputs, then run it on demand and see every run.'
}, {
  id: 'pulses',
  label: 'Pulses',
  icon: Repeat,
  copy: 'Work that runs without being asked — on a schedule, reporting into the channel that needs it.'
}, {
  /* Labelled for what the screen actually is. "Team workspaces" described a
   container; the view beneath it is a membership and access table, and
   access is the thing a company managing AI is looking for here. */
  id: 'shared',
  label: 'Groups & permissions',
  icon: Users,
  copy: 'Groups keep work, files, and connections together, and membership decides who can reach them — not personal accounts.'
}, {
  id: 'chats',
  label: 'AI chats',
  icon: MessageSquare,
  copy: 'A familiar chat interface, except it is signed into your tools. Enough to replace most standalone ChatGPT use.'
}];

/**
 * One row treatment for every list in these screens. A faint warm fill plus a
 * 1px contact shadow gives each row a surface, which is the difference between
 * a product screenshot and an outlined box.
 */
const ROW = 'rounded-xl border border-[#EFEAE1] bg-[#FCFAF6] shadow-[0_1px_0_rgba(255,255,255,0.9)_inset,0_1px_2px_-1px_rgba(43,33,26,0.06)]';

/**
 * WHERE THE LIGHT LANDS ON EACH SCREEN.
 *
 * One fixed focus point meant every screen was lit in the middle and faded at
 * the bottom — which only works if every screen's argument happens to live in
 * the middle. It does not. Chats makes its point in the answer on the left;
 * Skills makes it in the first rows, where the sharing sits; Workflows makes
 * it in the run history. Each screen therefore declares its own focus, so the
 * lit region is the claim and the fall-off takes the chrome that surrounds it.
 */
const FOCUS: Record<PrimitiveId, {
  x: number;
  y: number;
  size: string;
}> = {
  skills: {
    x: 46,
    y: 30,
    size: '74% 58%'
  },
  workflows: {
    x: 50,
    y: 30,
    size: '78% 62%'
  },
  /*
   * PULSES IS LIT ON THE LIVE ROW, not the header.
   *
   * The claim is "this is happening right now, without anyone asking" — and
   * the evidence is the third row, mid-run. Lighting the top of the panel put
   * the heading in the light and let the running pulse fall towards the dark,
   * which is precisely backwards. The focus sits on that row instead, so the
   * fall-off takes the schedule rows above and below it.
   */
  pulses: {
    x: 48,
    y: 62,
    size: '84% 72%'
  },
  /*
   * GROUPS IS THE ONE SCREEN THAT MUST NOT BE CUT.
   *
   * A fade across the last row says "the list carries on out of sight" — which
   * is right for a library (skills and templates genuinely continue) and
   * exactly wrong here. This screen's claim is that access is knowable: every
   * group, and how a person gets into each one. Dimming the final row argues
   * there is access you cannot see, which is the fear the screen exists to
   * answer. So the lit area is larger than the panel: the fall-off lands
   * outside the artwork, on the page, and no row is touched.
   */
  shared: {
    x: 50,
    y: 48,
    size: '108% 116%'
  },
  chats: {
    x: 40,
    y: 42,
    size: '80% 68%'
  }
};
export function PlatformPillar() {
  const [active, setActive] = useState<PrimitiveId>('skills');
  return <div data-id="element-890" data-mp="12oy2bh7hibt">
      {/* No eyebrow, and left-aligned to match the co-worker section. The
       "Workspace" label named the section before the headline could say
       anything, and the headline already states the claim. Left-aligning also
       puts the intro on the same edge as the primitives list directly beneath
       it, so the whole section reads down one axis instead of the copy being
       centred above a left-aligned column. */}
      <SectionIntro className="max-w-3xl text-left" data-id="element-891" data-mp="k0i07bk09o85">
        <h2 className="font-title text-4xl font-bold leading-[1.05] tracking-tight text-[#2B211A] md:text-[56px]" data-id="element-892" data-mp="ozm9e26xjlyh">
          One place to manage
          <br data-id="element-893" data-mp="1codwif5nxe4e" />
          AI within your company.
        </h2>
        <p className="mt-5 max-w-[54ch] text-lg leading-relaxed text-[#6B5D52]" data-id="element-894" data-mp="1hgouvdkr8ruu">
          Let your power users share their skills with other team members. Build automations in plain English. Schedule them with Pulses. Everything managed in one place, that you own, in the cloud.
        </p>
      </SectionIntro>

      <div className="mt-12 grid items-start gap-8 md:mt-16 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)] lg:gap-12" data-id="element-895" data-mp="3bvm1m2pynn29">
        {/* Primitives as a list you step through: only the active one carries
         its explanation, so five ideas never shout at once. */}
        <ul className="space-y-1.5" data-id="element-896" data-mp="2e952qt1kx1ee">
          {PRIMITIVES.map(p => {
          const on = active === p.id;
          return <li key={p.id} data-id="element-897" data-mp="2s966qhd54oy3">
                {/* Selection is carried by an orange rail and a warm fill
                 rather than a floating white card — the card read as a second
                 panel competing with the app frame beside it. */}
                <button type="button" onClick={() => setActive(p.id)} aria-current={on ? 'true' : undefined} className={`relative w-full rounded-xl py-4 pl-5 pr-4 text-left transition-[background-color,color] duration-200 ease-out ${on ? 'bg-white shadow-[0_1px_2px_-1px_rgba(43,33,26,0.08)] ring-1 ring-[#EFEAE1]' : 'hover:bg-[#F6F2EA]'}`} data-id="element-898" data-mp="7kidv3uzra1w">
                  <span className={`absolute bottom-3 left-0 top-3 w-[3px] rounded-full transition-colors duration-200 ease-out ${on ? 'bg-[#EA580C]' : 'bg-transparent'}`} aria-hidden="true" data-id="element-899" data-mp="368yryhg0u6x3" />
                  <span className="flex items-center gap-2.5" data-id="element-900" data-mp="2dl2sj4z2i0wm">
                    <p.icon size={17} strokeWidth={1.9} className={on ? 'text-[#EA580C]' : 'text-neutral-400'} data-id="element-901" data-mp="2diq5weid72d8" />
                    <span className={`font-title text-xl font-bold tracking-tight ${on ? 'text-[#2B211A]' : 'text-[#6B5D52]'}`} data-id="element-902" data-mp="2ihjmifsy79hm">
                      {p.label}
                    </span>
                  </span>
                  {on && <motion.p initial={{
                opacity: 0,
                y: -4
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.22,
                ease: [0.23, 1, 0.32, 1]
              }} className="mt-2.5 pl-[27px] text-[15px] leading-relaxed text-[#6B5D52]" data-id="element-903" data-mp="1uul95r04khnx">
                      {p.copy}
                    </motion.p>}
                </button>
              </li>;
        })}
        </ul>

        {/* No app nav on any of the five. The numbered rail to the left is
         already the navigation for this section, so a sidebar inside the frame
         put two columns of it around one screen — and each screen states what
         it is in its own heading anyway. */}
        {/* THE SAME VIGNETTE AS THE CONNECTIONS SCREEN, adapted to a column.
         Five app screens presented as a bordered card on white read as five
         screenshots; the point of the section is that they are five views of
         one system. Dissolving the frame into the page — and letting it run
         past the column on the right, where nothing sits beside it — makes it
         a window onto a larger product instead.
         It can only bleed RIGHT: the numbered rail occupies the left column,
         and both the artwork and the scrim would run over it. The scrim is
         asymmetric for the same reason — it out-reaches the bleed on the
         right, but stops just short of the rail on the left, where a
         white-on-white wash would fade the labels the reader is choosing
         between. */}
        <ProductVignette ground="#FFFFFF" focusX={FOCUS[active].x} focusY={FOCUS[active].y} focusSize={FOCUS[active].size} bleedClass="xl:-mr-[7%] 2xl:-mr-[10%]" scrimClass="-left-[3%] -right-[10%] -top-12 bottom-[-12%] xl:-right-[16%]" data-id="element-904" data-mp="1puf90ej2s6nw">
        <AppChrome sidebar={false} data-id="element-905" data-mp="1gus6tqiiuwts">
          <motion.div key={active} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.24,
            ease: [0.23, 1, 0.32, 1]
          }} className={active === 'chats' ? 'flex h-full flex-col' : 'px-6 py-6 md:px-9 md:py-8'} data-id="element-906" data-mp="1grnyqi1xrgxu">
            {active === 'chats' && <ChatsScreen data-id="element-907" data-mp="s9nixtgmnohl" />}
            {active === 'shared' && <SharedScreen data-id="element-908" data-mp="safdr22ehi6t" />}
            {active === 'workflows' && <WorkflowsScreen data-id="element-909" data-mp="nat0wtqhi3yj" />}
            {active === 'pulses' && <PulsesScreen data-id="element-910" data-mp="126ii8ekq5swx" />}
            {active === 'skills' && <SkillsScreen data-id="element-911" data-mp="128d2dqo69gzy" />}
          </motion.div>
        </AppChrome>
        </ProductVignette>
      </div>
    </div>;
}
function ScreenHead({
  title,
  meta
}: {
  title: string;
  meta: string;
}) {
  // A real app view states what you're looking at and then rules off. The
  // hairline is what stops these reading as floating wireframe blocks.
  return <div className="mb-6 border-b border-[#F1ECE3] pb-4" data-id="element-912" data-mp="1kxt60mv5lpkl">
      <h3 className="font-title text-[24px] font-semibold leading-tight tracking-[-0.015em] text-[#2B211A]" data-id="element-913" data-mp="1kzclib8zjwlb">
        {title}
      </h3>
      <p className="mt-1.5 text-[12.5px] tabular-nums text-neutral-500" data-id="element-914" data-mp="1txoj4x89run9">
        {meta}
      </p>
    </div>;
}

// The answer, as the thing a revenue lead would actually want on screen: which
// accounts, what they're worth, and the one fact that makes each a risk. Prose
// alone made the reader reassemble a table in their head.
const AT_RISK = [{
  account: 'Northwind Labs',
  value: '$84k',
  closes: '12 Sep',
  reason: 'No exec contact in 41 days · 2 tickets open'
}, {
  account: 'Meridian Health',
  value: '$61k',
  closes: '28 Sep',
  reason: 'Champion left in July · no replacement named'
}, {
  account: 'Fairline Group',
  value: '$37k',
  closes: '30 Sep',
  reason: 'Usage down 38% month over month'
}];
export function ChatsScreen() {
  return <div className="flex h-full min-h-[420px] flex-col" data-id="element-915" data-mp="3t7z845fv93yb">
      {/* Thread header — what this chat is and which group owns it. */}
      <div className="flex shrink-0 items-center justify-between gap-4 border-b border-[#F1ECE3] px-5 py-3.5 md:px-7" data-id="element-916" data-mp="3k6huctpxlecs">
        <div className="min-w-0" data-id="element-917" data-mp="2ctfwvwlvlo60">
          <h3 className="truncate font-title text-[16px] font-semibold tracking-[-0.01em] text-[#2B211A]" data-id="element-918" data-mp="2cuhgipgyjizc">
            Renewal risk in Salesforce
          </h3>
          <p className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-neutral-500" data-id="element-919" data-mp="o9tt6cwd632z">
            {/* Sales, in Sales' colour — the same teal the Groups screen
             gives it, so the chat's owner is recognisable at a glance. */}
            <span className="flex size-4 items-center justify-center rounded text-[7px] font-bold text-white" style={{
            backgroundColor: HUES.teal.solid
          }} data-id="element-920" data-mp="x8f71uqw4txf">
              SL
            </span>
            Sales
            <span className="text-[#D8CFC2]" data-id="element-921" data-mp="1byuwxmu94q1x">·</span>
            Today, 08:42
          </p>
        </div>
        <span className="hidden shrink-0 items-center gap-1.5 rounded-full border border-[#EFEAE1] bg-white px-2.5 py-1 text-[11px] font-medium text-neutral-500 sm:inline-flex" data-id="element-922" data-mp="1g0gevmln7uiz">
          <span className="size-1.5 rounded-full bg-emerald-500" data-id="element-923" data-mp="1kxgkjwekyv9k" />
          8 tools connected
        </span>
      </div>

      {/* Transcript */}
      <div className="flex-1 space-y-5 overflow-hidden px-5 py-5 md:px-7" data-id="element-924" data-mp="3mq4j3sga4uj0">
        <div className="flex justify-end" data-id="element-925" data-mp="tw5ykbklo46d">
          <p className="max-w-[80%] rounded-2xl rounded-br-md bg-[#F4F0E9] px-4 py-2.5 text-[13.5px] font-medium text-[#2B211A]" data-id="element-926" data-mp="tx8ooha3m2l8">
            Which renewals due this quarter look at risk?
          </p>
        </div>

        <div className="flex gap-3" data-id="element-927" data-mp="12yf7ix07zphn">
          <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg bg-[#2B211A]" data-id="element-928" data-mp="xyaq94rhmqqd">
            <Sparkles size={13} strokeWidth={2} className="text-white" data-id="element-929" data-mp="1curyvo8alr98" />
          </span>
          <div className="min-w-0 flex-1 space-y-3.5" data-id="element-930" data-mp="17wgp1dhzzgr0">
            {/* What it read, before what it concluded. */}
            <div className="relative space-y-1.5 pl-3.5 before:absolute before:bottom-1.5 before:left-0 before:top-1.5 before:w-[2px] before:rounded-full before:bg-[#EFEAE1] before:content-['']" data-id="element-931" data-mp="1lnv9lorjzq06">
              {[['salesforce', 'Read 34 opportunities closing this quarter'], ['intercom', 'Checked support volume per account'], ['gmail', 'Scanned 60 days of thread activity']].map(([slug, label]) => <p key={label} className="flex items-center gap-2 text-[12px] text-neutral-500" data-id="element-932" data-mp="2a8ro43wjab5x">
                  <span className="flex size-[18px] shrink-0 items-center justify-center rounded-md border border-[#EFEAE1] bg-white" data-id="element-933" data-mp="2f7b83yrkhcvn">
                    <BrandLogo slug={slug} className="size-[11px]" data-id="element-934" data-mp="209iujc5kyyk3" />
                  </span>
                  {label}
                  <Check size={11} strokeWidth={3} className="shrink-0 text-emerald-500" data-id="element-935" data-mp="257kedzatz1wm" />
                </p>)}
            </div>

            <p className="text-[13.5px] leading-relaxed text-[#3D3229]" data-id="element-936" data-mp="1w8qkwhssqrna">
              <strong className="font-semibold text-[#2B211A]" data-id="element-937" data-mp="1wa1pgju2g3fg">
                Three of eleven look at risk
              </strong>{' '}
              — $182k of $604k closing this quarter.
            </p>

            <ul className="divide-y divide-[#F1ECE3] overflow-hidden rounded-xl border border-[#EFEAE1] bg-[#FCFAF6]" data-id="element-938" data-mp="1mgt8x8tyrvni">
              {AT_RISK.map(r => <li key={r.account} className="flex items-start gap-3 px-3.5 py-2.5" data-id="element-939" data-mp="1cl9fct2vh91x">
                  <span className="mt-[5px] size-1.5 shrink-0 rounded-full bg-red-400" data-id="element-940" data-mp="15h9xlof335lg" />
                  <span className="min-w-0 flex-1" data-id="element-941" data-mp="10igijrk5f6j6">
                    <span className="block truncate text-[13px] font-semibold text-[#2B211A]" data-id="element-942" data-mp="10jrkdlu1a47n">
                      {r.account}
                    </span>
                    <span className="block truncate text-[11.5px] text-neutral-500" data-id="element-943" data-mp="qswyiikp96mq">
                      {r.reason}
                    </span>
                  </span>
                  <span className="shrink-0 text-right" data-id="element-944" data-mp="moyym5njvrsm">
                    <span className="block text-[13px] font-semibold tabular-nums text-[#2B211A]" data-id="element-945" data-mp="hqxgpo1v1yiq">
                      {r.value}
                    </span>
                    <span className="block text-[11px] tabular-nums text-neutral-400" data-id="element-946" data-mp="cup4aif1sjf9">
                      {r.closes}
                    </span>
                  </span>
                </li>)}
            </ul>

            <p className="text-[13px] text-neutral-500" data-id="element-947" data-mp="227skyqpl8cdu">
              I've drafted outreach for each owner — want me to send it?
            </p>
            {/* Offering the next move as buttons is what makes this a working
             surface rather than a transcript of one. */}
            <div className="flex flex-wrap gap-2" data-id="element-948" data-mp="2eo1r249201hc">
              <span className="rounded-lg bg-[#2B211A] px-3 py-1.5 text-[12px] font-semibold text-white" data-id="element-949" data-mp="2ji61wylfwtnp">
                Send all three
              </span>
              <span className="rounded-lg border border-[#E7E0D4] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#2B211A]" data-id="element-950" data-mp="1rpykvx20vf8s">
                Review drafts
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Composer */}
      <div className="shrink-0 px-5 pb-5 md:px-7" data-id="element-951" data-mp="20p25j5gu82pe">
        <div className="flex items-center gap-2.5 rounded-2xl border border-[#E7E0D4] bg-white px-3.5 py-2.5 shadow-[0_1px_2px_rgba(43,33,26,0.05)]" data-id="element-952" data-mp="25pgagen56wj7">
          <Plus size={16} strokeWidth={1.9} className="shrink-0 text-neutral-400" data-id="element-953" data-mp="3c5k0t28p7zag" />
          <span className="min-w-0 flex-1 truncate text-[13px] text-neutral-400" data-id="element-954" data-mp="3h3lh5feqpki8">
            Ask anything across your connected tools
          </span>
          <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[#2B211A]" data-id="element-955" data-mp="2mhoahq3s6m1b">
            <ArrowUp size={13} strokeWidth={2.6} className="text-white" data-id="element-956" data-mp="31enfd67900s3" />
          </span>
        </div>
      </div>
    </div>;
}

// Named after the departments any company would recognise. The previous set
// ("Giga Master", "NoContact Ops") was Giga's own internal workspace, so a
// visitor had to decode what each one was before the permissions idea landed.
//
// Counts were the wrong content here: "14 tracks · 96 chats" says a group has
// stuff in it, which nobody doubted, and says nothing about what the screen is
// actually for. What a group IS, is a set of people plus the tools and work
// they can reach — so each card names those instead.
// ACCESS IS THE VARIABLE, so it has to vary. Every row carrying an identical
// "Members only" pill said nothing — a column where every cell reads the same
// is not information. Naming how each group is JOINED (invite only, by
// request, open to all) is the fact the screen exists to show, and the counts
// beside the name give each group a size so they read as real places rather
// than as four labels.
type GroupAccess = 'invite' | 'request' | 'open';
const GROUPS: Array<{
  name: string;
  color: string;
  access: GroupAccess;
  people: number;
  tracks: number;
  chats: number;
}> = [{
  /* A group is the one thing that recurs across every screen in this set, so
   its colour is its identity — Leadership is indigo in Groups, in Workflows,
   and anywhere else it appears. That only works if the hues are actually
   distinguishable, which four near-blacks were not. */
  name: 'Leadership',
  color: HUES.indigo.solid,
  access: 'invite',
  people: 5,
  tracks: 14,
  chats: 96
}, {
  name: 'Sales',
  color: HUES.teal.solid,
  access: 'request',
  people: 7,
  tracks: 33,
  chats: 214
}, {
  name: 'Marketing',
  color: HUES.clay.solid,
  access: 'open',
  people: 6,
  tracks: 12,
  chats: 88
}, {
  name: 'Customer Success',
  color: HUES.plum.solid,
  access: 'request',
  people: 4,
  tracks: 21,
  chats: 131
}];
const ACCESS: Record<GroupAccess, {
  label: string;
  icon: typeof Lock;
  className: string;
  iconClass: string;
}> = {
  invite: {
    label: 'Invite only',
    icon: Lock,
    className: 'border-[#EFEAE1] bg-[#FBF8F3] text-neutral-600',
    iconClass: 'text-neutral-400'
  },
  request: {
    label: 'By request',
    icon: UserPlus,
    className: 'border-[#EFEAE1] bg-[#FBF8F3] text-neutral-600',
    iconClass: 'text-neutral-400'
  },
  // The only open group is the only coloured pill — the exception is the thing
  // an admin scans this list for.
  open: {
    label: 'Open to all',
    icon: Users,
    className: 'border-[#F3DECB] bg-[#FFF6EF] text-[#C2410C]',
    iconClass: 'text-[#EA580C]'
  }
};
export function SharedScreen() {
  return <div data-id="element-957" data-mp="s5ftk989cnin">
      {/* Stated as completeness, matching the treatment: every group in the
       workspace, and the way into each one. A partial view of access is worth
       nothing to the person asking who can see what. */}
      <ScreenHead title="Groups & Permissions" meta="Every group in the workspace, who is in it, and how someone gets in" data-id="element-958" data-mp="14ijictpc6f0z" />
      {/* ONE COLUMN, not two. Access is a column of values you compare down the
       list; in a 2×2 grid there is no column to run your eye down. */}
      <ul className="space-y-2.5" data-id="element-959" data-mp="1iff2bh74lqvt">
        {GROUPS.map(g => {
        const access = ACCESS[g.access];
        const AccessIcon = access.icon;
        return <li key={g.name} className="flex items-center gap-3.5 rounded-2xl border border-[#EFEAE1] bg-[#FCFAF6] p-3.5 sm:gap-4 sm:p-4" data-id="element-960" data-mp="1s69sgwnhtamj">
              {/* A glyph, not initials. Two letters in a coloured tile read as
               somebody's avatar, and a group is not a person — the people icon
               says "several of us" at a glance. Squared off, where a person's
               avatar is round, so the two never get confused. */}
              <span className="flex size-11 shrink-0 items-center justify-center rounded-xl shadow-[0_1px_2px_rgba(43,33,26,0.25),0_0_0_1px_rgba(255,255,255,0.12)_inset]" style={{
            backgroundColor: g.color
          }} data-id="element-961" data-mp="373pxm0em91mg">
                <Users size={18} strokeWidth={2} className="text-white" data-id="element-962" data-mp="3g5fqzbs26amv" />
              </span>

              <div className="min-w-0 flex-1" data-id="element-963" data-mp="3l036il533upb">
                <p className="truncate text-[15px] font-bold leading-tight text-[#2B211A]" data-id="element-964" data-mp="3q228prp38lko">
                  {g.name}
                </p>
                <p className="mt-1 truncate text-[12.5px] tabular-nums text-neutral-500" data-id="element-965" data-mp="3uyizyurl3g1z">
                  {g.people} people <span className="text-neutral-300" data-id="element-966" data-mp="3nsn1qcjlrnl">·</span>{' '}
                  {g.tracks} tracks <span className="text-neutral-300" data-id="element-967" data-mp="7mhy3ca3yx42">·</span>{' '}
                  {g.chats} chats
                </p>
              </div>

              <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-semibold ${access.className}`} data-id="element-968" data-mp="hl7hxalkermy">
                <AccessIcon size={12} strokeWidth={2.2} className={access.iconClass} data-id="element-969" data-mp="rqyytw13v2e6" />
                {access.label}
              </span>
            </li>;
      })}
      </ul>
    </div>;
}
const OWNER_AVATAR = 'https://cdn.magicpatterns.com/patterns/generated-images/b394cd62-6cbf-40a2-98a1-4af4a23e89dd.jpg';

// Every step names the tools it touches — a step with no marks left a visible
// gap in the column and read as a missing image rather than a deliberate blank.
const STEP_TOOLS: string[][] = [['jira', 'linear'], ['intercom', 'salesforce'], ['notion'], ['slack', 'gmail']];
export function WorkflowsScreen() {
  const steps = ['Pull what shipped, slipped and stalled from Jira and Linear', 'Check every account flagged in support or gone quiet', 'Draft the update into the team space — wins, risks, decisions', 'Post to #team-update and email the leadership list'];
  return <div data-id="element-970" data-mp="1oelqlgn8hwla">
      {/* Name, then one line of what it produces, then the health of it as
       tags on the right. You should not have to read the steps to know why
       this workflow exists. */}
      <div className="mb-5 flex flex-wrap items-start justify-between gap-x-6 gap-y-4 border-b border-[#F1ECE3] pb-5" data-id="element-971" data-mp="3nlk1t5jd9e86">
        <div className="min-w-0" data-id="element-972" data-mp="38x61b19fa1us">
          <h3 className="font-title text-[24px] font-semibold leading-tight tracking-[-0.015em] text-[#2B211A]" data-id="element-973" data-mp="38vuvkvwf2k11">
            Monday morning update
          </h3>
          <p className="mt-1.5 flex items-center gap-1.5 text-[13px] text-[#6B5D52]" data-id="element-974" data-mp="2zvfzd1hwzkir">
            Every Monday, 08:00
            <span className="text-[#D8CFC2]" data-id="element-975" data-mp="34u0zz212w67q">·</span>
            <BrandLogo slug="slack" className="size-3.5" data-id="element-976" data-mp="2pzmr3ub8ad6s" />
            <span className="font-medium text-[#2B211A]" data-id="element-977" data-mp="2q17m1l1gfy56">#team-update</span>
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2" data-id="element-978" data-mp="2uzr59donyvtu">
          {/* RUN NOW BELONGS WITH THE TITLE, not under the steps. It is the
           screen's primary action, and at the foot of the panel it sat below
           four rows of detail — the last thing found, and the first thing the
           vignette's fall-off takes. Beside the name it reads as "this is the
           thing, and this is how you set it going". */}
          <span className="inline-flex items-center gap-2 rounded-full bg-[#2B211A] px-3.5 py-1.5 text-[12px] font-bold text-white shadow-[0_1px_2px_rgba(43,33,26,0.3),0_8px_18px_-10px_rgba(43,33,26,0.7)]" data-id="element-979" data-mp="2neotihckuz2l">
            <Play size={11} fill="currentColor" data-id="element-980" data-mp="2we1yawprazgz" />
            Run now
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-600/20 bg-emerald-50 px-2.5 py-1.5 text-[11.5px] font-semibold text-emerald-700" data-id="element-981" data-mp="2se9nedcw4h8g">
            <span className="size-1.5 rounded-full bg-emerald-500" data-id="element-982" data-mp="31eg0hy4off73" />
            Active
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#EFEAE1] bg-white px-2.5 py-1.5 text-[11.5px] font-semibold tabular-nums text-neutral-600" data-id="element-983" data-mp="1bz065ovevy19">
            <Repeat size={11} className="text-neutral-400" data-id="element-984" data-mp="18hcdxt6h2zs7" />
            26 runs
          </span>
          {/* The bar earns its place: 96% means more with 25 of 26 ticks
           filled beside it than as a number on its own. */}
          <span className="inline-flex items-center gap-2 rounded-full border border-[#EFEAE1] bg-white px-2.5 py-1.5 text-[11.5px] font-semibold tabular-nums text-neutral-600" data-id="element-985" data-mp="1if8oairl5qp9">
            <span className="flex items-end gap-[2px]" data-id="element-986" data-mp="pr32rtspjxod">
              {Array.from({
              length: 8
            }, (_, i) => <span key={i} className={`h-2.5 w-[2.5px] rounded-full ${i === 5 ? 'bg-red-400' : 'bg-emerald-500/80'}`} data-id="element-987" data-mp="up4lgdm81uzb" />)}
            </span>
            96%
          </span>
        </div>
      </div>

      {/* Who owns it and who can see it — the questions the steps can't
       answer. The read/write strip that sat here only restated them. */}
      <div className="mb-6 flex flex-wrap items-center gap-x-7 gap-y-3" data-id="element-988" data-mp="6y8wdo95hdvr">
        <span className="flex items-center gap-2.5" data-id="element-989" data-mp="by4yfhyaev8o">
          <img src={OWNER_AVATAR} alt="" aria-hidden="true" className="size-7 shrink-0 rounded-full object-cover shadow-[0_0_0_1px_rgba(43,33,26,0.08)]" data-id="element-990" data-mp="2p9fvj2i28eun" />
          <span data-id="element-991" data-mp="2l8m6u6wsmw0p">
            <span className="block text-[10px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-992" data-mp="2l5zy43y62agw">
              Owner
            </span>
            <span className="block text-[12.5px] font-semibold text-[#2B211A]" data-id="element-993" data-mp="2bapy1lrbwd5r">
              Sarah Chen
            </span>
          </span>
        </span>
        {/* "Shared with" read as the audience for the update — but the update
         goes to #team-update, which the subtitle already says. The useful fact
         is permission: who can run and edit this workflow. Same group tile the
         Groups screen uses, so a group reads as one object everywhere. */}
        <span className="flex items-center gap-2.5" data-id="element-994" data-mp="1nkcfqmyjpsod">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-xl shadow-[0_1px_2px_rgba(43,33,26,0.2)]" style={{
          backgroundColor: HUES.indigo.solid
        }} data-id="element-995" data-mp="1nj11tubdz4jj">
            <Users size={13} strokeWidth={2} className="text-white" data-id="element-996" data-mp="18wlxduh1dopp" />
          </span>
          <span data-id="element-997" data-mp="1dx01j1fioeig">
            <span className="block text-[10px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-998" data-mp="1dxj9bpbgdmir">
              Can run and edit
            </span>
            <span className="block text-[12.5px] font-semibold text-[#2B211A]" data-id="element-999" data-mp="13yak19rj8w2q">
              Leadership · 5 people
            </span>
          </span>
        </span>
        {/* The last run moved up with it: an action and the evidence it has
         been working belong together, and neither is footer material. */}
        <span data-id="element-1000" data-mp="1iwcmfgmtvbie">
          <span className="block text-[10px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-1001" data-mp="1iurwk4bgkgqv">
            Last run
          </span>
          <span className="block text-[12.5px] font-semibold tabular-nums text-[#2B211A]" data-id="element-1002" data-mp="1mvbq6xwtp289">
            Monday 09:00 · 1m 12s
          </span>
        </span>
      </div>

      <p className="mb-2.5 text-[10.5px] font-bold uppercase tracking-[0.12em] text-neutral-400" data-id="element-1003" data-mp="2feipbiycmli3">
        Steps
      </p>
      {/* A rail behind the numbers makes the steps read as one sequence
       rather than four unrelated cards. */}
      <ol className="relative space-y-2.5 before:absolute before:bottom-7 before:left-[30px] before:top-7 before:w-px before:bg-[#EFEAE1] before:content-['']" data-id="element-1004" data-mp="3v373m5jx9byj">
        {steps.map((s, i) => <li key={s} className={`relative flex items-center gap-3.5 px-4 py-3.5 ${ROW}`} data-id="element-1005" data-mp="8q8jdxdcndpe" data-mpi={i}>
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[#2B211A] text-[11px] font-bold tabular-nums text-white shadow-[0_1px_2px_rgba(43,33,26,0.3)]" data-id="element-1006" data-mp="3h5ilh5h8oix6">
              {i + 1}
            </span>
            <span className="flex-1 text-[13px] leading-snug text-[#3D3229]" data-id="element-1007" data-mp="3m4d2q2zk5zf6">
              {s}
            </span>
            {/* The tools each step touches, stated once where they're used
             rather than duplicated in a strip above the list. */}
            <span className="flex shrink-0 items-center gap-1.5" data-id="element-1008" data-mp="11dmb9h26xj2n">
              {(STEP_TOOLS[i] ?? []).map(t => <BrandLogo key={t} slug={t} className="size-[15px] opacity-80" data-id="element-1009" data-mp="2uyfuxn4019nf" />)}
            </span>
          </li>)}
      </ol>
    </div>;
}
export function PulsesScreen() {
  // A pulse only matters if you know where its output lands, so the
  // destination sits on the row with the mark of the tool it posts to.
  /*
   * FIRED COUNT AND AGE, because the tick strip cannot carry them.
   *
   * Twelve ticks answer "is this healthy lately" and nothing else — they are a
   * trend, and a trend has no volume and no age. A pulse that has fired 214
   * times since March is making a completely different claim from one that ran
   * twice last week, and that claim ("this has been quietly doing the job for
   * months") is the whole reason the primitive exists. So each row now states
   * the total and when it started, with the ticks kept as the recent trend.
   */
  const pulses: Array<{
    name: string;
    cadence: string;
    next: string;
    to: string;
    logo: string;
    fired: number;
    since: string;
    health: number[];
    running?: string;
    step?: number;
    steps?: number;
  }> = [{
    name: 'Monday revenue brief',
    cadence: 'Mondays, 08:00',
    next: 'in 2 days',
    to: '#revenue',
    logo: 'slack',
    fired: 38,
    since: 'Jan 8',
    health: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
  }, {
    name: 'Ad spend anomaly check',
    cadence: 'Daily, 07:30',
    next: 'in 14 hours',
    to: 'Leadership list',
    logo: 'gmail',
    fired: 214,
    since: 'Feb 2',
    health: [1, 1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 2]
  }, {
    name: 'Support escalation sweep',
    cadence: 'Every 4 hours',
    next: 'in 51 minutes',
    to: '#support-escalations',
    logo: 'slack',
    fired: 1284,
    since: 'Nov 12',
    // Running right now. State belongs on the thing it describes, not in a
    // banner underneath that made you match a name back to a row.
    running: 'reading Intercom',
    step: 2,
    steps: 4,
    health: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
  }, {
    name: 'Churn signal digest',
    cadence: 'Fridays, 16:00',
    next: 'in 6 days',
    to: 'Customer success',
    logo: 'microsoftteams',
    fired: 31,
    since: 'Feb 20',
    health: [1, 1, 1, 1, 0, 1, 1, 1, 1, 1, 1, 1]
  }];
  // One starts paused, so the control reads as a real state rather than a
  // decorative button — and the paused row visibly stops reporting.
  const [paused, setPaused] = React.useState<Record<string, boolean>>({
    'Churn signal digest': true
  });
  /* The running row's two pulsing marks are the only motion on this screen,
   and both are captured mid-cycle by the export walk — a live dot frozen at
   40% opacity reads as a rendering fault in a still. Held at full strength
   for exports and for reduced motion, where the finished state is what is
   wanted anyway. */
  const still = useStill();
  const pulseClass = still ? '' : 'animate-pulse';
  return <div data-id="element-1010" data-mp="270dniviyejir">
      {/* The subtitle defines the primitive rather than counting rows: someone
       meeting Pulses for the first time needs to know what one is, and the
       list below already shows how many are running. */}
      <ScreenHead title="Pulses" meta="Work that runs on a schedule and reports into the channel that needs it" data-id="element-1011" data-mp="rhxz5hkcu37e" />
      <ul className="space-y-2.5" data-id="element-1012" data-mp="hq1vzs3evmwb">
        {pulses.map(p => {
        const off = !!paused[p.name];
        const running = off ? null : p.running ?? null;
        return <li key={p.name} className={`relative flex flex-wrap items-center gap-3.5 overflow-hidden px-4 py-3.5 ${ROW} ${running ? 'border-[#EA580C]/35 bg-[#FFF8F3] shadow-[0_1px_2px_-1px_rgba(43,33,26,0.06),0_0_0_3px_rgba(234,88,12,0.07)]' : ''}`} data-id="element-1013" data-mp="3opkptz749gr">
              {/* RUNNING IS A STATE OF THE WHOLE ROW, not a word in a column.
               A small spinner beside the name was competing with eleven other
               marks; an accent rail down the row's own edge changes the object
               rather than adding to it, which is what "this one is live right
               now" has to do to be seen at a glance. */}
              {running && <span aria-hidden="true" className="absolute inset-y-0 left-0 w-[3px] bg-[#EA580C]" data-id="element-1014" data-mp="1mp899xjsse1b" />}
              <span className="min-w-0 flex-1" data-id="element-1015" data-mp="1hplv9lw5atrg">
                <span className={`block truncate text-[13.5px] font-semibold ${off ? 'text-[#8C7F73]' : 'text-[#2B211A]'}`} data-id="element-1016" data-mp="1hqds101haxjb">
                  {p.name}
                </span>
                {/* While a pulse is mid-run, the row reports the run instead of
                 the schedule — the schedule is not the useful fact right now.
                 The step bar is the part that makes it unmistakable: two
                 segments done, one working, one to go, so the row shows
                 PROGRESS rather than merely asserting activity. */}
                {running ? <span className="mt-1 flex items-center gap-2 text-[11.5px] font-semibold text-[#C2410C]" data-id="element-1017" data-mp="2t5kc4bxkek38">
                    <span className="flex shrink-0 items-center gap-[3px]" aria-hidden="true" data-id="element-1018" data-mp="2oe279csf7zcz">
                      {Array.from({
                  length: p.steps ?? 0
                }, (_, i) => <span key={i} className={`h-[5px] w-4 rounded-full ${i < (p.step ?? 0) ? 'bg-[#EA580C]' : i === (p.step ?? 0) ? `${pulseClass} bg-[#EA580C]/55` : 'bg-[#EA580C]/15'}`} data-id="element-1019" data-mp="30oa0gfl4jea1" />)}
                    </span>
                    <span className="truncate" data-id="element-1020" data-mp="3amzkadwkz8sx">
                      Step {(p.step ?? 0) + 1} of {p.steps} · {running}
                    </span>
                  </span> : <span className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-neutral-500" data-id="element-1021" data-mp="3fjzris562hlm">
                    {p.cadence}
                    <span className="text-[#D8CFC2]" data-id="element-1022" data-mp="3odmafho9zqde">·</span>
                    <BrandLogo slug={p.logo} className={`size-3.5 ${off ? 'opacity-40' : ''}`} data-id="element-1023" data-mp="3og7ac150jw8i" />
                    <span className="truncate" data-id="element-1024" data-mp="3tfbj4qo0zryb">{p.to}</span>
                  </span>}
              </span>
              {/* Twelve ticks say "is this healthy" faster than a percentage. */}
              <span className="hidden shrink-0 items-end gap-[3px] rounded-md bg-white px-1.5 py-1 ring-1 ring-[#F1ECE3] sm:flex" data-id="element-1025" data-mp="768zuu094t7p">
                {p.health.map((h, i) => <span key={i} className={`h-3.5 w-[3px] rounded-full ${off ? 'bg-[#E2DACE]' : h === 1 ? 'bg-emerald-500/80' : h === 0 ? 'bg-red-500/85' : 'bg-amber-500/85'}`} data-id="element-1026" data-mp="yz2uyt8g48cn" data-mpi={i} />)}
              </span>
              {/* The record: how many times it has fired, and since when. */}
              <span className="hidden w-[104px] shrink-0 text-right sm:block" data-id="element-1027" data-mp="kyi7vkvhzqdc">
                <span className={`block text-[14px] font-semibold tabular-nums leading-none ${off ? 'text-[#8C7F73]' : 'text-[#2B211A]'}`} data-id="element-1028" data-mp="l0cxvdlmm8oa">
                  {p.fired.toLocaleString()}
                </span>
                <span className="mt-1 block text-[10px] font-medium tabular-nums text-neutral-400" data-id="element-1029" data-mp="twvfvusbkc63">
                  runs since {p.since}
                </span>
              </span>
              <span className="flex w-[96px] shrink-0 items-center justify-end gap-1.5 text-[11.5px] font-medium tabular-nums text-neutral-500" data-id="element-1030" data-mp="25gg99d7552wt">
                {off ? <span className="text-neutral-400" data-id="element-1031" data-mp="28w17kdrt3nvv">Paused</span> : running ? <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EA580C] px-2.5 py-1 text-[11px] font-bold text-white" data-id="element-1032" data-mp="28ynfieim05em">
                    <span className={`size-1.5 rounded-full bg-white ${pulseClass}`} data-id="element-1033" data-mp="1yz6ck4uqs5mc" />
                    Running
                  </span> : <>
                    <Clock size={11} className="text-neutral-300" data-id="element-1034" data-mp="23zakh6hl3s0y" />
                    {p.next}
                  </>}
              </span>
              <button type="button" onClick={() => setPaused(s => ({
            ...s,
            [p.name]: !off
          }))} aria-label={off ? `Resume ${p.name}` : `Pause ${p.name}`} className="flex size-7 shrink-0 items-center justify-center rounded-lg border border-[#EFEAE1] bg-white text-[#6B5D52] transition-colors duration-150 ease-out hover:border-[#D8CFC2] hover:text-[#2B211A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#EA580C]/40" data-id="element-1035" data-mp="2wmeqneseyceb">
                {off ? <Play size={12} fill="currentColor" data-id="element-1036" data-mp="3fe7obmifglsd" /> : <Pause size={12} fill="currentColor" data-id="element-1037" data-mp="3p8lgi0fm0aw6" />}
              </button>
            </li>;
      })}
      </ul>
    </div>;
}

// One sparkle on every row said "AI feature" four times and nothing about the
// skills themselves. A glyph per subject makes the list scannable by what each
// method is for, and the ink tile matches the group tiles on the Groups screen
// so both screens read as the same application.
export function SkillsScreen() {
  // Named as the piece of knowledge itself ("Inbound lead qualification")
  // rather than as a sentence about it ("How we qualify an inbound lead") —
  // a library of nouns scans; a list of sentences has to be read.
  const skills: Array<{
    name: string;
    icon: React.ElementType;
    /* A skill's hue comes from the group it belongs to, so colour carries
     meaning instead of being decoration: scan the tiles and you can see which
     part of the company owns which method. */
    hue: {
      solid: string;
      tint: string;
      ink: string;
    };
    used: number;
    group: string;
    by: string;
    initials: string;
    members: number;
    team: string[];
    what?: string;
    trend?: number[];
  }> = [{
    name: 'Inbound lead qualification',
    /* Only the featured skill carries a line of description. Giving all four
     one would flatten the screen back into four equal blocks, which is what
     made it read as an enterprise list rather than a product. */
    what: 'Scores a new lead against our ICP, checks the account in Salesforce, and drafts the first reply.',
    icon: Filter,
    used: 142,
    trend: [3, 5, 4, 7, 6, 9, 8, 12],
    group: 'Marketing',
    hue: HUES.clay,
    by: 'Priya Nair',
    initials: 'PN',
    /* WHO CAN RUN IT — the whole point of a skill, and the thing the previous
     version of this screen never showed. It listed an author and a reuse
     count, which describes one person's document; sharing is what turns that
     into something the company owns. */
    members: 24,
    team: ['JR', 'AB', 'MK', 'DS']
  }, {
    name: 'Renewal email drafting',
    icon: Mail,
    used: 211,
    group: 'Sales',
    hue: HUES.teal,
    by: 'Marcus Lee',
    initials: 'ML',
    members: 31,
    team: ['LC', 'TW', 'RG']
  }, {
    name: 'Weekly reporting format',
    icon: BarChart3,
    used: 96,
    group: 'Leadership',
    hue: HUES.indigo,
    by: 'Sarah Chen',
    initials: 'SC',
    members: 8,
    team: ['SC', 'PN']
  }, {
    name: 'P1 incident escalation',
    icon: Siren,
    used: 38,
    group: 'Customer Success',
    hue: HUES.plum,
    by: 'Tom Alvarez',
    initials: 'TA',
    members: 17,
    team: ['TA', 'NB', 'EM']
  }];
  const [featured, ...rest] = skills;
  return <div data-id="element-1038" data-mp="3dv5zmscekqoy">
      {/*
       * ONE FEATURED SKILL, THEN THE LIBRARY.
       *
       * Four identical beige rows is what made this read as old: equal weight
       * everywhere is an unmade decision, and a stack of bordered boxes in a
       * column is a filing cabinet. The screen now has a subject — one skill
       * given a real card, its description, its share and its trend — with the
       * others as a quiet hairline-separated list beneath. Same information,
       * but there is something to look at first.
       */}
      <div className="mb-5 flex flex-wrap items-end justify-between gap-x-6 gap-y-3 border-b border-[#F1ECE3] pb-4" data-id="element-1039" data-mp="1a905euvrpkt">
        <div className="min-w-0" data-id="element-1040" data-mp="6bg7xdx0worh">
          <h3 className="font-title text-[24px] font-semibold leading-tight tracking-[-0.015em] text-[#2B211A]" data-id="element-1041" data-mp="3npv157a5al1e">
            Skills
          </h3>
          <p className="mt-1.5 text-[12.5px] text-neutral-500" data-id="element-1042" data-mp="3si4qom7clj35">
            One person writes down how they do something. Their whole group can
            run it.
          </p>
        </div>
        {/* The two controls a real library has. They are what stops the screen
         reading as a static list of four things. */}
        <div className="flex shrink-0 items-center gap-2" data-id="element-1043" data-mp="39qc4bamsaw4e">
          <span className="hidden items-center gap-2 rounded-full border border-[#EFE7DA] bg-white px-3 py-1.5 text-[11.5px] text-neutral-400 sm:flex" data-id="element-1044" data-mp="39pspi2sfknul">
            <Search size={12} strokeWidth={2.2} data-id="element-1045" data-mp="9lri5tw1ton3" />
            Search 34 skills
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#2B211A] px-3.5 py-1.5 text-[12px] font-bold text-white shadow-[0_1px_2px_rgba(43,33,26,0.3),0_8px_18px_-10px_rgba(43,33,26,0.7)]" data-id="element-1046" data-mp="3r20xv603bl37">
            <Plus size={12} strokeWidth={2.6} data-id="element-1047" data-mp="3i0jmu21ji9ld" />
            New skill
          </span>
        </div>
      </div>

      {/* THE FEATURED CARD. White on the warm ground, a genuinely layered warm
       shadow, and the only element on the screen carrying an accent. */}
      <article className="rounded-2xl border border-[#EFE7DA] bg-white p-4 shadow-[0_1px_2px_rgba(43,33,26,0.04),0_10px_26px_-16px_rgba(43,33,26,0.22)]" data-id="element-1048" data-mp="38585rd2nsqag">
        <div className="flex items-start gap-3.5" data-id="element-1049" data-mp="16cjxgpb5rcnt">
          {/* A tinted tile with a saturated glyph, not a black square with a
           white one. A column of ink tiles was the single biggest reason these
           lists read as flat — same shape, same value, four times over. */}
          <span className="flex size-10 shrink-0 items-center justify-center rounded-2xl" style={{
          backgroundColor: featured.hue.tint,
          boxShadow: `inset 0 0 0 1px ${featured.hue.solid}22`
        }} data-id="element-1050" data-mp="2qucfb6v4vyaq">
            <featured.icon size={17} strokeWidth={2} style={{
            color: featured.hue.ink
          }} data-id="element-1051" data-mp="30qyzrujjsnc8" />
          </span>
          <div className="min-w-0 flex-1" data-id="element-1052" data-mp="3el89ubqz57lo">
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1" data-id="element-1053" data-mp="39ku1stpdhxnn">
              <h4 className="font-title text-[16px] font-semibold tracking-[-0.01em] text-[#2B211A]" data-id="element-1054" data-mp="3oh1n5xcip54i">
                {featured.name}
              </h4>
              <span className="inline-flex items-center gap-1 rounded-full bg-[#FDF0E6] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.07em] text-[#C2410C]" data-id="element-1055" data-mp="3jirpng3blsfq">
                Most used
              </span>
            </div>
            <p className="mt-1.5 max-w-[52ch] text-[12.5px] leading-relaxed text-[#6B5D52]" data-id="element-1056" data-mp="1p6jy8cq7jtop">
              {featured.what}
            </p>
          </div>
          {/* The trend column: the number, and eight weeks of it underneath.
           A count alone says "popular"; the climb says "spreading". */}
          <div className="hidden shrink-0 text-right sm:block" data-id="element-1057" data-mp="2ss4c54gkus6l">
            <span className="block text-[20px] font-semibold tabular-nums leading-none tracking-[-0.02em] text-[#2B211A]" data-id="element-1058" data-mp="2stfdyyqgppv2">
              {featured.used}
            </span>
            <span className="mt-1 block text-[9.5px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-1059" data-mp="31rr1grw187ja">
              runs
            </span>
            <span className="mt-2 flex items-end justify-end gap-[3px]" aria-hidden="true" data-id="element-1060" data-mp="36lv9zfky4nmh">
              {featured.trend?.map((h, i) => <span key={i} className="w-[3px] rounded-full bg-[#EA580C]" style={{
              height: `${6 + h * 1.6}px`,
              opacity: 0.35 + i * 0.08
            }} data-id="element-1061" data-mp="3ghymnfdfc4no" />)}
            </span>
          </div>
        </div>

        {/* The handover, spelled out on its own line where there is room for
         it to read as a sentence rather than a chip. */}
        <div className="mt-3.5 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-[#F4EEE5] pt-3.5" data-id="element-1062" data-mp="14wg03xmxd0vu">
          <span className="flex items-center gap-2 text-[11.5px] text-neutral-500" data-id="element-1063" data-mp="zx3aj54o5fpk">
            <span className="flex size-[19px] shrink-0 items-center justify-center rounded-full text-[7.5px] font-bold text-white" style={{
            backgroundColor: avatarHue(featured.initials)
          }} data-id="element-1064" data-mp="uy9ylh515wri">
              {featured.initials}
            </span>
            Written by
            <span className="font-semibold text-[#2B211A]" data-id="element-1065" data-mp="1isbbliezv9p7">{featured.by}</span>
          </span>
          <span className="flex items-center gap-2 text-[11.5px] text-neutral-500" data-id="element-1066" data-mp="1dui4mn5vabnv">
            <UserPlus size={13} strokeWidth={2.2} className="text-[#EA580C]" aria-hidden="true" data-id="element-1067" data-mp="2bb76gguthdx9" />
            Shared into
            {/* The group chip carries the group's own colour, so "Marketing"
             is the same object here as on the Groups screen. */}
            <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-bold" style={{
            backgroundColor: featured.hue.tint,
            color: featured.hue.ink
          }} data-id="element-1068" data-mp="1w1bivx7x5ngj">
              <span className="size-1.5 rounded-full" style={{
              backgroundColor: featured.hue.solid
            }} data-id="element-1069" data-mp="1m1t4dwas2tvv" />
              {featured.group}
            </span>
            <span className="flex -space-x-1.5" aria-hidden="true" data-id="element-1070" data-mp="2er07z9p3a9nz">
              {featured.team.map(m => <span key={m} className="flex size-[19px] items-center justify-center rounded-full text-[7px] font-bold text-white ring-2 ring-white" style={{
              backgroundColor: avatarHue(m)
            }} data-id="element-1071" data-mp="202nau9e9mkrw">
                  {m}
                </span>)}
            </span>
            {featured.members} can run it
          </span>
        </div>
      </article>

      {/* THE REST AS A LIST, not as cards. Hairlines instead of four more
       borders: the eye runs down one column of names, and the featured card
       above stays the only object with edges. */}
      <ul className="mt-1" data-id="element-1072" data-mp="h1lcjce62y4e">
        {rest.map(s => <li key={s.name} className="flex items-center gap-3.5 border-b border-[#F4EEE5] py-3 last:border-b-0" data-id="element-1073" data-mp="vsvc58bp357t">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-xl" style={{
          backgroundColor: s.hue.tint,
          boxShadow: `inset 0 0 0 1px ${s.hue.solid}22`
        }} data-id="element-1074" data-mp="quk2oypbawq4">
              <s.icon size={14} strokeWidth={2} style={{
            color: s.hue.ink
          }} data-id="element-1075" data-mp="3ubj0scpl8lnl" />
            </span>
            <span className="min-w-0 flex-1" data-id="element-1076" data-mp="c5xpp3n5qh22">
              <span className="block truncate text-[13px] font-semibold text-[#2B211A]" data-id="element-1077" data-mp="82hu8amtqmqy">
                {s.name}
              </span>
              <span className="mt-0.5 flex items-center gap-1.5 truncate text-[11.5px] text-neutral-500" data-id="element-1078" data-mp="1nigoqj3iqk8q">
                {s.by}
                <span className="text-[#D8CFC2]" data-id="element-1079" data-mp="1jk97zo921ixj">·</span>
                <span className="inline-flex items-center gap-1 font-semibold" style={{
              color: s.hue.ink
            }} data-id="element-1080" data-mp="g67irzzz56xx">
                  <span className="size-1.5 shrink-0 rounded-full" style={{
                backgroundColor: s.hue.solid
              }} data-id="element-1081" data-mp="l8gayu50w4yz" />
                  {s.group}
                </span>
                <span className="text-[#D8CFC2]" data-id="element-1082" data-mp="u68ru2qe7koh">·</span>
                {s.members} can run it
              </span>
            </span>
            <span className="hidden shrink-0 -space-x-1.5 sm:flex" aria-hidden="true" data-id="element-1083" data-mp="2fm7awcunza2">
              {s.team.map(m => <span key={m} className="flex size-[19px] items-center justify-center rounded-full text-[7px] font-bold text-white ring-2 ring-[#FCFAF6]" style={{
            backgroundColor: avatarHue(m)
          }} data-id="element-1084" data-mp="6hpjxob999wx">
                  {m}
                </span>)}
            </span>
            <span className="w-[52px] shrink-0 text-right text-[12.5px] font-semibold tabular-nums text-[#2B211A]" data-id="element-1085" data-mp="3atuprblnr9oi">
              {s.used}
              <span className="ml-1 font-medium text-neutral-400" data-id="element-1086" data-mp="37cowwleoh5rl">runs</span>
            </span>
          </li>)}
      </ul>
    </div>;
}